/**
 * Add Element Modal - allows users to insert survey elements via drag-and-drop or click
 */
import React, { useState } from "react"
import ReactDOM from "react-dom"
import { Input } from "antd"
import { Editor as TiptapEditor } from "@tiptap/react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faTimes,
  faSearch,
  faPlusCircle,
} from "@fortawesome/free-solid-svg-icons"
import actions from "../Editor/MenuBar/MenuActions"
import { useDraggablePanel } from "hooks/core/useDraggablePanel"

interface AddElementModalProps {
  isVisible: boolean
  onClose: () => void
  editor: TiptapEditor | null
}

interface TemplateItem {
  key: string
  title: string
  description: string
  category: string
  icon: string
}

const templates: TemplateItem[] = [
  {
    key: "note",
    title: "Note",
    description: "Add internal notes for programmers or stakeholders",
    category: "Documentation",
    icon: "📝",
  },
  {
    key: "section",
    title: "Section",
    description: "Organize questionnaire into sections (like chapters)",
    category: "Structure",
    icon: "📂",
  },
  {
    key: "text",
    title: "Text Block",
    description: "Add text that will be shown to respondents",
    category: "Content",
    icon: "📄",
  },
  {
    key: "simple_list",
    title: "Simple List",
    description: "Basic list with options for questions",
    category: "Lists",
    icon: "📋",
  },
  {
    key: "list_with_various_representations",
    title: "List with Representations",
    description: "List with multiple textual representations",
    category: "Lists",
    icon: "📋",
  },
  {
    key: "exception_list",
    title: "Exception List",
    description:
      "List for exceptions like 'None of the above', 'Prefer not to say'",
    category: "Lists",
    icon: "⚠️",
  },
  {
    key: "fill_in_number",
    title: "Fill in Number",
    description: "Question where respondent enters a number",
    category: "Questions",
    icon: "🔢",
  },
  {
    key: "fill_in_text",
    title: "Fill in Text",
    description: "Question where respondent enters open-ended text",
    category: "Questions",
    icon: "✏️",
  },
  {
    key: "single_select_basic",
    title: "Single Select (Basic)",
    description: "Single choice question with inline options",
    category: "Questions",
    icon: "⚪",
  },
  {
    key: "single_select_user_predefined_list",
    title: "Single Select (List)",
    description: "Single choice question using a predefined list",
    category: "Questions",
    icon: "⚪",
  },
  {
    key: "multiple_select_basic",
    title: "Multiple Select (Basic)",
    description: "Multiple choice question with inline options",
    category: "Questions",
    icon: "☑️",
  },
  {
    key: "multiple_select_user_predefined_list",
    title: "Multiple Select (List)",
    description: "Multiple choice question using a predefined list",
    category: "Questions",
    icon: "☑️",
  },
  {
    key: "multiple_select_extended_with_predefined_list",
    title: "Multiple Select (Extended)",
    description: "Multiple choice with textual representations",
    category: "Questions",
    icon: "☑️",
  },
  {
    key: "grid_single_select_basic",
    title: "Grid Single Select (Basic)",
    description: "Single choice grid with inline options",
    category: "Grid Questions",
    icon: "🔲",
  },
  {
    key: "grid_single_select_user_predefined_list",
    title: "Grid Single Select (List)",
    description: "Single choice grid using predefined lists",
    category: "Grid Questions",
    icon: "🔲",
  },
  {
    key: "grid_single_select_extended_with_predefined_list",
    title: "Grid Single Select (Extended)",
    description: "Single choice grid with textual representations",
    category: "Grid Questions",
    icon: "🔲",
  },
  {
    key: "grid_multiple_select_basic",
    title: "Grid Multiple Select (Basic)",
    description: "Multiple choice grid with inline options",
    category: "Grid Questions",
    icon: "☑️",
  },
  {
    key: "grid_multiple_select_user_predefined_list",
    title: "Grid Multiple Select (List)",
    description: "Multiple choice grid using predefined lists",
    category: "Grid Questions",
    icon: "☑️",
  },
  {
    key: "grid_multiple_select_extended_with_predefined_list",
    title: "Grid Multiple Select (Extended)",
    description: "Multiple choice grid with textual representations",
    category: "Grid Questions",
    icon: "☑️",
  },
  {
    key: "numerical_grid",
    title: "Numerical Grid",
    description: "Grid where the respondent enters a number for each row",
    category: "Grid Questions",
    icon: "🔢",
  },
  {
    key: "hidden_question",
    title: "Hidden Question",
    description: "Question hidden from respondents but captured in data",
    category: "Advanced",
    icon: "👁️",
  },
  {
    key: "data",
    title: "Data Field",
    description: "Store additional data not from question responses",
    category: "Advanced",
    icon: "💾",
  },
  {
    key: "hide_a_question",
    title: "Hide Question",
    description: "Hide a question in a specific variant",
    category: "Variants",
    icon: "🚫",
  },
  {
    key: "extend_a_list",
    title: "Extend List",
    description: "Add options to a list in a variant",
    category: "Variants",
    icon: "➕",
  },
  {
    key: "trim_a_list",
    title: "Trim List",
    description: "Remove options from a list in a variant",
    category: "Variants",
    icon: "✂️",
  },
  {
    key: "reposition_questions",
    title: "Reposition Questions",
    description: "Change question position in a variant",
    category: "Variants",
    icon: "↕️",
  },
  {
    key: "recode",
    title: "Recode",
    description:
      "Derive or auto-code a variable from existing answers (opens the Recode Wizard)",
    category: "Logic",
    icon: "🔁",
  },
]

export const AddElementModal: React.FC<AddElementModalProps> = ({
  isVisible,
  onClose,
  editor,
}) => {
  const [searchTerm, setSearchTerm] = useState("")
  const { position, isDragging, handleHeaderMouseDown } = useDraggablePanel()

  const filteredTemplates = templates.filter(
    (template) =>
      template.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.category.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const handleTemplateClick = (templateKey: string) => {
    const actionMap = actions(editor)
    const action = actionMap[templateKey as keyof typeof actionMap]
    if (action && typeof action === "function") {
      action()
      onClose()
    }
  }

  const handleDragStart = (e: React.DragEvent, templateKey: string) => {
    e.dataTransfer.effectAllowed = "copy"
    e.dataTransfer.setData("application/template", templateKey)
    e.dataTransfer.setData("text/plain", templateKey) // Fallback
  }

  // Group templates by category
  const categorizedTemplates = filteredTemplates.reduce((acc, template) => {
    if (!acc[template.category]) {
      acc[template.category] = []
    }
    acc[template.category].push(template)
    return acc
  }, {} as Record<string, TemplateItem[]>)

  if (!isVisible) return null

  const panelContent = (
    <div
      className="bg-white rounded-lg shadow-2xl z-[1000] pointer-events-auto"
      style={{
        position: "fixed",
        left: position.x !== 0 ? `${position.x}px` : undefined,
        top: position.y !== 0 ? `${position.y}px` : "100px",
        right: position.x !== 0 ? "auto" : "20px",
        width: "320px",
        maxHeight: "80vh",
        border: "1px solid #D1D5DB",
        cursor: isDragging ? "grabbing" : undefined,
      }}
    >
      {/* Header */}
      <div
        className="px-4 py-3 rounded-t-lg flex items-center justify-between cursor-grab active:cursor-grabbing"
        style={{ backgroundColor: "var(--dq-brand)" }}
        onMouseDown={handleHeaderMouseDown}
      >
        <div className="flex items-center gap-2 text-white">
          <FontAwesomeIcon icon={faPlusCircle} />
          <span className="font-semibold">Add Survey Element</span>
        </div>
        <button
          onClick={onClose}
          className="text-white hover:text-gray-200 transition-colors"
          aria-label="Close panel"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>

      {/* Content */}
      <div className="p-3">
        <div className="mb-2">
          <Input
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            prefix={
              <FontAwesomeIcon icon={faSearch} style={{ color: "var(--dq-brand)" }} />
            }
            allowClear
            size="small"
          />
        </div>

        <div className="text-xs text-gray-500 mb-2">
          Click to insert or drag to tree
        </div>

        <div className="flex flex-col gap-2 max-h-[calc(80vh-150px)] overflow-y-auto">
          {Object.entries(categorizedTemplates).map(([category, items]) => (
            <div key={category} className="flex flex-col gap-1">
              <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide m-2 mb-0.5">
                {category}
              </h4>
              <div className="flex flex-col gap-1.5">
                {items.map((template) => (
                  <div
                    key={template.key}
                    className="border border-gray-200 rounded p-2 hover:bg-cyan-50 hover:border-cyan-300 transition-all cursor-pointer"
                    draggable
                    onClick={() => handleTemplateClick(template.key)}
                    onDragStart={(e) => handleDragStart(e, template.key)}
                  >
                    <div className="flex items-start gap-1.5">
                      <div className="text-base flex-shrink-0 mt-0.5">
                        {template.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-gray-800">
                          {template.title}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5 leading-tight">
                          {template.description}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {filteredTemplates.length === 0 && (
          <div className="text-center text-gray-500 py-8">
            No templates found matching "{searchTerm}"
          </div>
        )}
      </div>
    </div>
  )

  // Use portal to render at document body level so it appears on the right side of screen
  return ReactDOM.createPortal(panelContent, document.body)
}

export default AddElementModal
